import { useMutation } from '@apollo/client/react';
import { Button, CloseButton, Dialog, HStack, Portal, Text, Textarea, VStack } from '@chakra-ui/react';
import { Field } from '@src/components/chakra-ui/field';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { UPSERT_RATING } from '@src/gql/mutations/UpsertRating';
import { ScoAdvisorSession } from '@src/graphql-types';
import { useEffect, useState } from 'react';
import { LuStar } from 'react-icons/lu';
const SCORES = [1, 2, 3, 4, 5] as const;

/** Public review text cap (L38). `Rating.note` is the public review. */
export const SESSION_REVIEW_TEXT_MAX_LENGTH = 500;
type SessionRatingModalProps = {
  open: boolean;
  session: ScoAdvisorSession;
  counterpartName: string;
  onOpenChange: (open: boolean) => void;
  onRated?: () => void;
};
export const SessionRatingModal = ({
  open,
  session,
  counterpartName,
  onOpenChange,
  onRated
}: SessionRatingModalProps) => {
  const existing = session.currentUserRating;
  const [score, setScore] = useState<number>(existing?.score ?? 0);
  const [note, setNote] = useState(existing?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    setScore(existing?.score ?? 0);
    setNote(existing?.note ?? '');
    setError(null);
  }, [open, existing?.score, existing?.note]);
  const [upsertRating, upsertState] = useMutation(UPSERT_RATING);
  const handleSubmit = async () => {
    if (score < 1 || score > 5) {
      setError('Pick a rating from 1 to 5.');
      return;
    }
    if (note.trim().length > SESSION_REVIEW_TEXT_MAX_LENGTH) {
      setError(`Keep the review to ${SESSION_REVIEW_TEXT_MAX_LENGTH} characters.`);
      return;
    }
    setError(null);
    try {
      await upsertRating({
        variables: {
          input: {
            rateableId: session.advisorSessionId,
            rateableType: 'AdvisorSession',
            score,
            note: note.trim() || null
          }
        }
      });
      onRated?.();
      onOpenChange(false);
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not save that rating.'));
    }
  };
  return <Dialog.Root motionPreset={'scale'} open={open} placement={'center'} scrollBehavior={'inside'} size={'md'} onOpenChange={event => onOpenChange(event.open)}>
      <Portal>
        <Dialog.Backdrop bg={'blackAlpha.700'} />
        <Dialog.Positioner paddingX={4}>
          <Dialog.Content bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={'2xl'} borderWidth={'1px'} boxShadow={'xl'} maxWidth={'480px'} width={'full'}>
            <Dialog.Header alignItems={'flex-start'} borderBottomWidth={'1px'} borderColor={'colorPalette.border/50'} paddingBottom={4} paddingTop={5} paddingX={6} position={'relative'}>
              <VStack align={'flex-start'} flex={1} gap={1} minWidth={0} paddingRight={10}>
                <Dialog.Title fontSize={'xl'} fontWeight={'semibold'} lineHeight={'short'}>
                  Rate this session
                </Dialog.Title>
                <Dialog.Description color={'colorPalette.fg/60'} fontSize={'sm'}>
                  How was your session with {counterpartName}?
                </Dialog.Description>
              </VStack>
              <Dialog.CloseTrigger asChild insetEnd={3} position={'absolute'} top={3}>
                <CloseButton size={'sm'} />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body paddingBottom={4} paddingTop={5} paddingX={6}>
              <VStack align={'stretch'} gap={4}>
                <HStack gap={1} justify={'center'}>
                  {SCORES.map(value => {
                  const selected = value <= score;
                  return <Button key={value} aria-label={`${value} star${value === 1 ? '' : 's'}`} aria-pressed={score === value} color={selected ? 'orange.fg' : 'fg.muted'} css={{
                    '& svg': {
                      fill: selected ? 'currentColor' : 'none'
                    }
                  }} size={'lg'} variant={'ghost'} onClick={() => setScore(value)}>
                        <LuStar />
                      </Button>;
                })}
                </HStack>
                <Field helperText={`Optional. A public review of the session, up to ${SESSION_REVIEW_TEXT_MAX_LENGTH} characters. ${note.length}/${SESSION_REVIEW_TEXT_MAX_LENGTH}`} label={'Shown on their profile'}>
                  <Textarea maxLength={SESSION_REVIEW_TEXT_MAX_LENGTH} placeholder={'What stood out about the session?'} rows={4} value={note} onChange={event_0 => setNote(event_0.target.value)} />
                </Field>
                {error && <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                    {error}
                  </Text>}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderColor={'colorPalette.border/50'} borderTopWidth={'1px'} gap={2} paddingX={6} paddingY={4}>
              <Button variant={'ghost'} onClick={() => onOpenChange(false)}>
                Not now
              </Button>
              <Button loading={upsertState.loading} variant={'cta'} onClick={() => void handleSubmit()}>
                Save rating
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>;
};